import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { lang, setLang, ui } = useLang();

    const LINKS = [
        { label: ui.nav.produzioni, href: "#produzioni", testid: "nav-link-produzioni" },
        { label: ui.nav.chiSiamo, href: "#chi-siamo", testid: "nav-link-chi-siamo" },
        { label: ui.nav.team, href: "#team", testid: "nav-link-team" },
        { label: ui.nav.stampa, href: "#stampa", testid: "nav-link-stampa" },
        { label: ui.nav.contatti, href: "#contatti", testid: "nav-link-contatti" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    const LangToggle = ({ testid }) => (
        <div data-testid={testid} className="flex items-center rounded-full border border-white/15 p-1">
            {["it", "en"].map((l) => (
                <button
                    key={l}
                    data-testid={`lang-${l}-button`}
                    onClick={() => setLang(l)}
                    className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                        lang === l ? "bg-brand text-white" : "text-slate-400 hover:text-white"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled
                    ? "border-b border-white/10 bg-black/70 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
                <button
                    data-testid="nav-logo-button"
                    onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                    className="flex items-center gap-3"
                    aria-label="RED PRIVATE SRL — torna su"
                >
                    <img src="/logo.png" alt="Logo RED PRIVATE SRL" className="h-9 w-auto" />
                    <span className="font-display text-lg font-extrabold tracking-[0.18em] text-white">
                        RED<span className="text-brand">PRIVATE</span>
                    </span>
                </button>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={l.testid}
                            onClick={() => go(l.href)}
                            className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <LangToggle testid="lang-toggle-desktop" />
                    <button
                        data-testid="nav-cta-button"
                        onClick={() => go("#contatti")}
                        className="group flex items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
                    >
                        {ui.nav.cta}
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                </nav>

                <div className="flex items-center gap-3 lg:hidden">
                    <LangToggle testid="lang-toggle-mobile" />
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
                        aria-label="Apri menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="border-b border-white/10 bg-ink/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {LINKS.map((l, i) => (
                                <motion.button
                                    key={l.href}
                                    data-testid={`nav-mobile-${l.testid}`}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    onClick={() => go(l.href)}
                                    className="py-3 text-left font-display text-2xl font-bold text-white"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="nav-mobile-cta-button"
                                onClick={() => go("#contatti")}
                                className="mt-4 rounded-full bg-brand px-6 py-3.5 font-semibold text-white"
                            >
                                {ui.nav.cta}
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
